import { useEffect, useRef, useState, Suspense } from 'react';
import styles from './Hero.module.css';
import { lazy } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useVoiceAgent } from '../../../pages/VoiceAgentPage/context/VoiceAgentContext';
import { START_VOICE_CONVERSATION_STATE_KEY } from '../../../hooks/useEnquiry';
import VinCharacter from '../../../assets/icons/vin-character.svg';

const VoiceAgentPage = lazy(() => import('../../../pages/VoiceAgentPage/VoiceAgentPage'));

/* ── School logos ────────────────────────────────────────── */
const SCHOOL_LOGOS = [
  { src: '/assets/school-logo-1.png', alt: 'The Heritage School' },
  { src: '/assets/school-logo-2.png', alt: 'Suchitra Academy' },
  { src: '/assets/school-logo-4.png', alt: 'The Shri Ram Universal School' },
  { src: '/assets/school-logo-5.png', alt: 'Meridian School, Kukatpally' },
  { src: '/assets/school-logo-6.png', alt: 'Lilawati Vidya Mandir School' },
  { src: '/assets/school-logo-7.png', alt: 'NGIS' },
  { src: '/assets/school-logo-8.png', alt: 'Accord School' },
  { src: '/assets/school-logo-10.png', alt: 'Hayde Heritage Academy, Kotdwara' },
  { src: '/assets/school-logo-11.png', alt: 'Laurel High Global School' },
  { src: '/assets/school-logo-12.png', alt: "Mother Mary's School, Mayur Vihar" },
  { src: '/assets/school-logo-14.png', alt: 'Bellwether International School' },
  { src: '/assets/school-logo-15.png', alt: "Balaji's Highfields International School" },
  { src: '/assets/school-logo-16.png', alt: 'CGR International School' },
  { src: '/assets/school-logo-17.png', alt: 'Delhi Public School, Varanasi' },
  { src: '/assets/school-logo-18.png', alt: 'Little Woods School' },
  { src: '/assets/school-logo-19.png', alt: 'Meena Bhujbal School of Excellence' },
  { src: '/assets/school-logo-20.png', alt: 'Magadham International School' },
  { src: '/assets/school-logo-21.png', alt: 'Magadham Vidyapeeth' },
  { src: '/assets/school-logo-22.png', alt: 'Mahavir Senior Model School' },
  { src: '/assets/school-logo-23.png', alt: 'Nopany High School' },
  { src: '/assets/school-logo-24.png', alt: 'Ortus International School' },
  { src: '/assets/school-logo-25.png', alt: 'Shri Harkrishan Sahib Public High School' },
  { src: '/assets/school-logo-26.png', alt: 'Silver Oaks International School, Oakhills' },
  { src: '/assets/school-logo-27.png', alt: 'Sr. Capital Sr. Sec. School' },
  { src: '/assets/school-logo-28.png', alt: 'STEM School, Vidya Nagar' },
  { src: '/assets/school-logo-29.png', alt: 'Shri Harkrishan Sahib Public Sr. Sec. School' },
  { src: '/assets/school-logo-30.png', alt: 'Vista International School' },
  { src: '/assets/school-logo-31.png', alt: 'WoodRock World School' },
  { src: '/assets/school-logo-32.png', alt: 'Westberry School, Tenali' },
  { src: '/assets/school-logo-33.png', alt: 'Horizon Valley Future School' },
];

const HINT_TEXTS = [
  'What is a future ready school?',
  'How can Coschool help my School?',
  'How does it improve class averages?',
];

export default function Hero({ handleBtnClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const logoSet = [...SCHOOL_LOGOS, ...SCHOOL_LOGOS];
  const sectionRef = useRef(null);
  const startBtnRef = useRef(null);
  const [hintIndex, setHintIndex] = useState(0);
  const [hintVisible, setHintVisible] = useState(true);

  // Marquee pauses while the carousel is hovered or touched
  const [carouselPaused, setCarouselPaused] = useState(false);

  const [showVoiceAgent, setShowVoiceAgent] = useState(false);
  const { initiateConv, currentStep } = useVoiceAgent();

  const handleTalkToVin = () => {
    setShowVoiceAgent(true);
    initiateConv(true);
  };

  useEffect(() => {
    if (showVoiceAgent && currentStep === 0) {
      setShowVoiceAgent(false);
    }
  }, [currentStep, showVoiceAgent]);

  useEffect(() => {
    const handler = () => handleTalkToVin();
    window.addEventListener('startVoiceConversation', handler);
    return () => window.removeEventListener('startVoiceConversation', handler);
  }, []);

  useEffect(() => {
    if (location.state?.[START_VOICE_CONVERSATION_STATE_KEY]) {
      handleTalkToVin();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  /* ── Interactive gradient blobs: follow pointer subtly ── */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const orbs = section.querySelectorAll('[data-orb]');
    let rafId = null;

    const handlePointer = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        // Normalise pointer to -1…1 range within section
        const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;

        orbs.forEach((orb, i) => {
          // Each orb moves a different amount for parallax feel
          const factor = (i + 1) * 12;
          orb.style.transform = `translate(${nx * factor}px, ${ny * factor}px)`;
        });
        rafId = null;
      });
    };

    section.addEventListener('pointermove', handlePointer);
    return () => {
      section.removeEventListener('pointermove', handlePointer);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  /* ── Rotating hint text ──────────────────────────────────── */
  useEffect(() => {
    const interval = setInterval(() => {
      setHintVisible(false);
      setTimeout(() => {
        setHintIndex((prev) => (prev + 1) % HINT_TEXTS.length);
        setHintVisible(true);
      }, 300); // brief pause then fade in next
    }, 3000); // total display time per hint
    return () => clearInterval(interval);
  }, []);

  if (showVoiceAgent) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <VoiceAgentPage />
      </Suspense>
    );
  }

  return (
    <section id="hero" className={styles.section} aria-label="Hero" ref={sectionRef}>
      {/* Background gradient blobs */}
      <div className={styles.orbTop} data-orb aria-hidden="true" />
      <div className={styles.orbBottomLeft} data-orb aria-hidden="true" />
      <div className={styles.orbBottomRight} data-orb aria-hidden="true" />

      <div className={styles.inner}>

        {/* ── Text column ───────────────────────────────── */}
        <div className={styles.textCol}>
          <h1 className={styles.heading}>
            Trusted AI Partner for{' '}
            <span className={styles.shimmer}>future&#8209;ready schools</span>
          </h1>

          <p className={styles.subtext}>
            Enabling schools across India to become AI-powered
          </p>

          {/* School logo carousel */}
          <div
            className={styles.carouselWrapper}
            aria-label="Partner schools"
            // Hover is mouse-only: touch fires emulated mouse events that would
            // otherwise leave the marquee stuck paused after a tap
            onPointerEnter={(e) => e.pointerType === 'mouse' && setCarouselPaused(true)}
            onPointerLeave={(e) => e.pointerType === 'mouse' && setCarouselPaused(false)}
            onTouchStart={() => setCarouselPaused(true)}
            onTouchEnd={() => setCarouselPaused(false)}
            onTouchCancel={() => setCarouselPaused(false)}
          >
            <div
              className={`${styles.carouselTrack} ${carouselPaused ? styles.carouselPaused : ''}`}
            >
              {logoSet.map((logo, i) => (
                <div
                  key={i}
                  className={styles.logoSlot}
                  aria-hidden={i >= SCHOOL_LOGOS.length}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <img
                    src={logo.src}
                    alt={i < SCHOOL_LOGOS.length ? logo.alt : ''}
                    width="57"
                    height="57"
                    loading="eager"
                    decoding="sync"
                    draggable="false"
                    className={styles.schoolLogo}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Desktop-only CTA — hidden on mobile */}
          <a href="#cta" className={`wm-btn-primary ${styles.cta} ${styles.ctaDesktop}`} aria-label="Learn more about CoSchool">
            About Us
          </a>
        </div>

        {/* ── Bottom section: connector + prompt + CTA ── */}
        <div className={styles.mediaCol}>
          <div className={styles.connectorArea}>
            {/* Connector line */}
            <div className={styles.connector} aria-hidden="true" />

            {/* CTA button with Vin character */}
            <div className={styles.ctaGroup}>
              <div className={styles.vinCharacter}>
                <img
                  src="/assets/vin-image.png"
                  alt="Vin — CoSchool assistant"
                  className={styles.vinImg}
                />
              </div>
              <button
                ref={startBtnRef}
                className={styles.startBtn}
                type="button"
                onClick={() => handleBtnClick?.(startBtnRef.current, 'Start Conversation')}
              >
                <span className={styles.startBtnLabel}>Talk to Vin</span>
                <img
                  src={VinCharacter}
                  alt=""
                  className={styles.eqIcon}
                />
              </button>
            </div>
            {/* Rotating hint text */}
            <p className={`${styles.hintText} ${hintVisible ? styles.hintVisible : styles.hintHidden}`}>
              &ldquo; {HINT_TEXTS[hintIndex]} &rdquo;
            </p>
          </div>
        </div>

      </div>

    </section>
  );
}
